import React, { lazy, Suspense, useEffect, useState } from 'react';
import codegen from 'codegen.macro';
import PropTypes from 'prop-types';
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
  useLocation,
  withRouter,
} from 'react-router-dom';
import { Redirector } from 'react-router-redirect';
import { useSelector } from 'react-redux';

import { AppLoading, Global404, Route404 } from 'ui-components/src';
import { initialiseEnv } from 'ui-common/src/lib/utils';
import Admin from 'ui-admin';

import {
  AAGRoutes,
  AAG_AUDIT_LOG_BASENAME,
  AAG_HEADER_URLS,
  AAG_INSIGHT_DASHBOARD_BASENAME,
  AAG_SOD_BASENAME,
  AAG_LICENSE_INTELLIGENCE_BASENAME,
} from '@saviynt/aag';
import { SSOListPage, ClientCredentialsListPage } from '@saviynt/auth';
import { isLoggedIn } from '@saviynt/common';
import { CertsRoutes } from '@saviynt/certs';
import { ExtRoutes, IDVRoutes, USERMANAGEMENT_BASE_URL } from '@saviynt/external';
import { mfaPrompt } from '@saviynt/nav';
import { PAMRoutes } from '@saviynt/pam';
import { RuleRecRoutes } from '@saviynt/rule-recs';
import { RoleRecRoutes } from '@saviynt/role-recs';
import {
  SAVI_ASSISTANT_BASENAME,
  SAVI_ASSISTANT_ALLOWED_URLS,
  SAVI_ASSISTANT_HEADER_URLS,
  SERVICE_IDENTITY_LIST,
  SERVICE_IDENTITY_ALLOWED_URLS,
  SERVICE_IDENTITY_HEADER_URLS,
} from '@saviynt/savicopilot';

const SaviCopilotRoutes = lazy(() =>
  import('@saviynt/savicopilot').then((mod) => ({ default: mod.SaviCopilotRoutes }))
);

import AuthRedirect from '../auth/AuthRedirect';
import BrandingConfig from '../features/AdminHome/views/BrandingConfig';
import { Control } from '../features/MainLayout/Control';
import { getModule } from './dynamic-modules';
import { Footer } from '../features/MainLayout/Footer';
import { Header } from '../features/MainLayout/Header';
import { Login } from '../features/MainLayout/Login';
import { RequestHome } from '../features/MainLayout/RequestHome';
import VersionPage from '../features/MainLayout/Version';
import Layout from '../Layout/Layout';
import LayoutWrapper from '../Layout/LayoutWrapper/LayoutWrapper';
import UserLogout from '../logout/UserLogout';
import SomethingWentWrong from '../logout/SomethingWentWrong';
import SessionTimeout from '../logout/SessionTimeout';
import SingleSpaMfeContainer from '../MicroFrontEnd/Component/SingleSpaMfeContainer';
import {
  AGENTS_MFE_CONFIG,
  AGENT_GATEWAYS_MFE_CONFIG,
  ISPM_MFE_CONFIG,
  ISPM_INTEGRATIONS_MFE_CONFIG,
  POLICIES_MFE_CONFIG,
  NHI_OLCM_MFE_CONFIG,
} from '../MicroFrontEnd/config/mfeConfigs';
import useAgentFeatureFlag from '../MicroFrontEnd/hooks/useAgentFeatureFlag';
import useIspmFeatureFlag from '../MicroFrontEnd/hooks/useIspmFeatureFlag';
import { getNavVisibility } from './unifiedNavVisibility';
import { useMfeHostStore } from '../utils/MfeHostStoreContext';

const PrivateRoute = withRouter(({ component: Component, location, ...rest }) => {
  const Child = (props) => (
    <LayoutWrapper>
      <Component {...props} />
    </LayoutWrapper>
  );
  return <Route render={(props) => (isLoggedIn() ? Child(props) : null)} {...rest} />;
});

// Pre-launch page manages its own layout — exclude from global header/footer

// Hides headers and footers on match
export const NAV_VIS_CONFIG = {
  base: '/ECMv6',
  header: {
    exact: [
      '/request/privilegedAccess/check-out-credential',
      '/request/privilegedAccess/connect-to-session',
      '/request/privilegedAccess/check-out-role',
    ],
    paramPatterns: ['/certification/details', '/campaign/prelaunch'],
  },
  footer: {
    exact: [
      '/request/privilegedAccess/check-out-credential',
      '/request/privilegedAccess/connect-to-session',
      '/request/privilegedAccess/check-out-role',
    ],
    paramPatterns: ['/certification/details', '/campaign/prelaunch'],
  },
};

const RenderHeader = () => {
  const location = useLocation();
  const leftNavGen2Flag = useSelector((state) => state.user.leftNavGen2);
  const isLeftNavGen2Enabled = Boolean(parseInt(leftNavGen2Flag, 10));
  const { hideHeader } = getNavVisibility(location.pathname, NAV_VIS_CONFIG);

  if (hideHeader) return null;
  if (isLeftNavGen2Enabled) return <Layout />;
  return <Header />;
};

const RenderFooter = () => {
  const location = useLocation();
  const leftNavGen2Flag = useSelector((state) => state.user.leftNavGen2);
  const isLeftNavGen2Enabled = Boolean(parseInt(leftNavGen2Flag, 10));
  const { hideFooter } = getNavVisibility(location.pathname, NAV_VIS_CONFIG);

  if (hideFooter) return null;
  if (isLeftNavGen2Enabled) return null;
  return <Footer />;
};

/** Layout wrapper for admin pages that need the header + top margin fix */
const AdminPageLayout = ({ children }) => (
  <>
    <RenderHeader />
    {/* 16px margin is workaround for layout issue with header */}
    <div style={{ marginTop: '16px' }}>{children}</div>
  </>
);

const SSOConfig = () => (
  <AdminPageLayout>
    <SSOListPage />
  </AdminPageLayout>
);

const OAuthClientsPage = () => (
  <AdminPageLayout>
    <ClientCredentialsListPage />
  </AdminPageLayout>
);

const UIBrandingPage = () => {
  return (
    <>
      <RenderHeader />
      <BrandingConfig />
    </>
  );
};

const MfeContainer = () => {
  const { snapshot } = useMfeHostStore();
  const { isHeaderVisible, isFooterVisible } = snapshot.ui.mfeLayout;

  return (
    <>
      {isHeaderVisible && <RenderHeader />}
      <div id="mfe-container">
        <AppLoading isMfe />
      </div>
      {isFooterVisible && <RenderFooter />}
    </>
  );
};

const RenderIspm = ({ location }) => (
  <SingleSpaMfeContainer
    packageName={ISPM_MFE_CONFIG.packageName}
    routePrefix={ISPM_MFE_CONFIG.routePrefix}
    additionalRoutePrefixes={ISPM_MFE_CONFIG.additionalRoutePrefixes}
    containerId={ISPM_MFE_CONFIG.containerId}
    errorContainerId={ISPM_MFE_CONFIG.errorContainerId}
    location={location}
    RenderHeader={RenderHeader}
    useFeatureFlag={useIspmFeatureFlag}
  />
);

const RenderIntegrations = ({ location }) => (
  <SingleSpaMfeContainer
    packageName={ISPM_INTEGRATIONS_MFE_CONFIG.packageName}
    routePrefix={ISPM_INTEGRATIONS_MFE_CONFIG.routePrefix}
    containerId={ISPM_INTEGRATIONS_MFE_CONFIG.containerId}
    errorContainerId={ISPM_INTEGRATIONS_MFE_CONFIG.errorContainerId}
    location={location}
    RenderHeader={RenderHeader}
    useFeatureFlag={useIspmFeatureFlag}
  />
);

const AgentHeader = ({ location }) => {
  const hideHeader =
    location?.pathname?.includes('/agent/request/create') ||
    location?.pathname?.includes('/agent/manage/request') ||
    location?.pathname?.includes('/agent/service-identity/create');

  return hideHeader ? null : <RenderHeader />;
};

const RenderAgent = ({ location }) => {
  const isServiceIdentityRoute = location?.pathname?.includes('/agent/service-identity');

  if (isServiceIdentityRoute) {
    return;
  }

  return (
    <SingleSpaMfeContainer
      packageName={AGENTS_MFE_CONFIG.packageName}
      routePrefix={AGENTS_MFE_CONFIG.routePrefix}
      containerId={AGENTS_MFE_CONFIG.containerId}
      errorContainerId={AGENTS_MFE_CONFIG.errorContainerId}
      location={location}
      RenderHeader={AgentHeader}
      useFeatureFlag={useAgentFeatureFlag}
    />
  );
};

const RenderAgentGateways = ({ location }) => (
  <SingleSpaMfeContainer
    packageName={AGENT_GATEWAYS_MFE_CONFIG.packageName}
    routePrefix={AGENT_GATEWAYS_MFE_CONFIG.routePrefix}
    containerId={AGENT_GATEWAYS_MFE_CONFIG.containerId}
    errorContainerId={AGENT_GATEWAYS_MFE_CONFIG.errorContainerId}
    location={location}
    RenderHeader={RenderHeader}
    useFeatureFlag={useIspmFeatureFlag}
  />
);

const RenderPolicies = ({ location }) => (
  <SingleSpaMfeContainer
    packageName={POLICIES_MFE_CONFIG.packageName}
    routePrefix={POLICIES_MFE_CONFIG.routePrefix}
    containerId={POLICIES_MFE_CONFIG.containerId}
    errorContainerId={POLICIES_MFE_CONFIG.errorContainerId}
    location={location}
    RenderHeader={RenderHeader}
    useFeatureFlag={useIspmFeatureFlag}
  />
);

const RenderNhiOlcm = ({ location }) => (
  <SingleSpaMfeContainer
    packageName={NHI_OLCM_MFE_CONFIG.packageName}
    routePrefix={NHI_OLCM_MFE_CONFIG.routePrefix}
    containerId={NHI_OLCM_MFE_CONFIG.containerId}
    errorContainerId={NHI_OLCM_MFE_CONFIG.errorContainerId}
    location={location}
    RenderHeader={RenderHeader}
    useFeatureFlag={useIspmFeatureFlag}
  />
);

const ExtRouteContent = () => {
  const externalClassificationInfo = useSelector((state) => state.user.externalClassificationInfo);
  const isExtClassificationEnabled = externalClassificationInfo?.externalClassification === '1';

  const location = useLocation();
  return (
    <>
      {isExtClassificationEnabled && <RenderHeader />}
      <ExtRoutes key={location.pathname} location={location} />
    </>
  );
};

const IDVRouteContent = () => {
  const isIDProofingEnabled = useSelector((state) => state.user.isIDProofingEnabled);
  const location = useLocation();
  return (
    <>
      {isIDProofingEnabled && <RenderHeader />}
      <IDVRoutes key={location.pathname} />
    </>
  );
};

const UserManagementRouteContent = () => {
  const location = useLocation();
  return (
    <>
      <RenderHeader />
      <ExtRoutes key={location.pathname} location={location} basename={USERMANAGEMENT_BASE_URL} />
    </>
  );
};

const RuleRecsRouteContent = () => {
  const location = useLocation();
  return (
    <>
      <RenderHeader />
      <RuleRecRoutes key={location.pathname} location={location} />
    </>
  );
};

const RoleRecsRouteContent = () => {
  const location = useLocation();
  return (
    <>
      <RenderHeader />
      <RoleRecRoutes key={location.pathname} location={location} />
    </>
  );
};

const RedirectRoute = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
      isLoggedIn() ? <Redirect to="/request/requestHome" /> : <Component {...props} />
    }
  />
);

RedirectRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
};

class DynamicModules extends React.Component {
  render() {
    const { location } = this.props;
    const loadedCSSClass = location.pathname.indexOf('mySessions') > 0 ? 'extendedTabHeight' : '';
    const loginClass = location.pathname === '/login' ? 'd-none' : '';
    const UserLogoutClass = location.pathname === '/logout' ? 'd-none' : '';

    return (
      <div className={`dynamicModules ${loadedCSSClass} ${loginClass} ${UserLogoutClass}`}>
        {codegen.require('./dynamicRouteCodegen')}
      </div>
    );
  }
}

DynamicModules.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  dynamicModules: PropTypes.func.isRequired,
};

DynamicModules.defaultProps = {
  dynamicModules: getModule,
};

const WithRouterModule = withRouter(DynamicModules);
const Routes = (props) => {
  const allowedRoutes = useSelector((state) => state.user.userModules);
  const appLoading = useSelector((state) => state.user.appLoading);

  let blockedUrlList = useSelector((state) => state.user.blockedUrlList);

  blockedUrlList = blockedUrlList
    ? blockedUrlList.map((r) => {
        if (r) return r.replace('/ECMv6', '');

        return r;
      })
    : null;
  const blockedNames = useSelector((state) => state.user.blockedNames);
  const domainInfo = useSelector((state) => state.user.domainInfo);
  const [allowedReactRoutes, setAllowedReactRoutes] = useState(['/login']);

  useEffect(() => {
    if (domainInfo && Object.keys(domainInfo).length) {
      initialiseEnv(domainInfo);
    }
  }, [domainInfo]);

  useEffect(() => {
    if (allowedRoutes.length === 0) {
      if (!appLoading && isLoggedIn()) {
        // App finished loading but user has no mapped features — redirect to access denied page
        window.location.href = '/ECM/login/denied';
      }
      return;
    }

    const allowedFilteredRoutes = allowedRoutes.map((r) => {
      if (r) {
        return r.replace('/ECMv6', '');
      }

      return r;
    });

    setAllowedReactRoutes([...allowedFilteredRoutes]);
  }, [allowedRoutes, appLoading]);

  return (
    <Router basename="/ECMv6">
      <Route404
        routes={{
          staticRoutes: allowedReactRoutes,
          dynamicRoutes: [
            '/request/privilegedAccess/launch',
            '/versionpage',
            '/',
            '/pam',
            '/certification',
            '/campaign/prelaunch', // Campaign pre-launch analysis page
            '/gen2',
            '/integrations',
            AAG_SOD_BASENAME,
            AAG_AUDIT_LOG_BASENAME,
            AAG_INSIGHT_DASHBOARD_BASENAME,
            AAG_LICENSE_INTELLIGENCE_BASENAME,
            '/intelligence/ispm',
            '/intelligence/integrations',
            '/intelligence/policies',
            '/admin/ispm-data-scope-management',
            '/nhi-olcm',
            '/agent',
            '/agent/registration',
            '/agent/manage',
            '/agent/request',
            '/agent-gateway',
            SAVI_ASSISTANT_BASENAME,
            SERVICE_IDENTITY_LIST,
            '/admin',
            '/request/privilegedAccess',
            '/mfe',
            '/migration',
            '/savi-ai',
          ],
          blockedUrlList,
          blockedNames,
        }}
        render={(path) => <Global404 path={path} />}
      >
        <PrivateRoute
          exact
          path="/"
          render={(args) => <Redirect to="/request/requestHome" {...args} />}
        />
        <Redirector />
        <PrivateRoute path="/certification" component={CertsRoutes} />
        <PrivateRoute path="/sso" component={SSOConfig} />
        {/* Campaign Pre-Launch Analysis — uses CertsRoutes with its own full-page layout */}
        <PrivateRoute path="/campaign/prelaunch" component={CertsRoutes} />
        <PrivateRoute exact path="/pam" component={RenderHeader} />
        <PrivateRoute path="/request" component={RenderHeader} />
        <PrivateRoute path="/review" component={RenderHeader} />
        <PrivateRoute path="/external" component={ExtRouteContent} />
        <PrivateRoute path="/idv" component={IDVRouteContent} />
        <PrivateRoute exact path="/usermanagement" component={UserManagementRouteContent} />
        <PrivateRoute path="/rule-recommendations" component={RuleRecsRouteContent} />
        <PrivateRoute path="/role-recommendations" component={RoleRecsRouteContent} />
        <mfaPrompt.Routes />
        <PrivateRoute path="/design" component={RenderHeader} />
        <PrivateRoute path="/control" component={RenderHeader} />
        <PrivateRoute path="/campaign" component={RenderHeader} />
        <PrivateRoute path="/request/privilegedAccess" component={PAMRoutes} />
        <PrivateRoute path="/request/jitAccess" component={PAMRoutes} />
        <PrivateRoute path="/identityMatchAndMerge/home" component={RenderHeader} />
        <RedirectRoute exact path="/login" component={Login} />
        <PrivateRoute exact path="/request/requestHome" component={RequestHome} />
        <PrivateRoute exact path="/versionpage" component={VersionPage} />
        <Route exact path="/auth" component={AuthRedirect} />
        <RedirectRoute exact path="/logout" component={UserLogout} />
        <RedirectRoute exact path="/somethingwentwrong" component={SomethingWentWrong} />
        <RedirectRoute exact path="/sessiontimeout" component={SessionTimeout} />
        <Switch>
          <PrivateRoute path="/admin/forms" component={MfeContainer} />
          <PrivateRoute path="/admin/credentials" component={MfeContainer} />
          <PrivateRoute path="/admin/orchestration" component={MfeContainer} />
          <PrivateRoute exact path="/admin/UIBranding" component={UIBrandingPage} />
          <PrivateRoute exact path="/admin/oauth/clients" component={OAuthClientsPage} />
          <PrivateRoute path="/admin" component={Admin} />
        </Switch>
        <PrivateRoute path="/request" component={RenderFooter} />
        <PrivateRoute path="/review" component={RenderFooter} />
        <PrivateRoute path="/design" component={RenderFooter} />
        <PrivateRoute path="/control" component={Control} />
        <PrivateRoute path="/campaign" component={RenderFooter} />
        <PrivateRoute path="/identityMatchAndMerge/home" component={RenderFooter} />
        <PrivateRoute path="/certification/list" component={RenderHeader} />
        <PrivateRoute
          path={[
            AAG_SOD_BASENAME,
            AAG_AUDIT_LOG_BASENAME,
            AAG_INSIGHT_DASHBOARD_BASENAME,
            AAG_LICENSE_INTELLIGENCE_BASENAME,
          ]}
          component={AAGRoutes}
        />
        <PrivateRoute exact path={AAG_HEADER_URLS} component={RenderHeader} />
        <PrivateRoute path={AAG_INSIGHT_DASHBOARD_BASENAME} component={RenderHeader} />
        {/* Savi Copilot */}
        <PrivateRoute
          path={[...SAVI_ASSISTANT_ALLOWED_URLS, ...SERVICE_IDENTITY_ALLOWED_URLS]}
          component={() => (
            <Suspense fallback={<AppLoading />}>
              <SaviCopilotRoutes />
            </Suspense>
          )}
        />
        <PrivateRoute
          exact
          path={[...SAVI_ASSISTANT_HEADER_URLS, ...SERVICE_IDENTITY_HEADER_URLS]}
          component={RenderHeader}
        />
        <Switch>
          {/* MFE's */}
          <PrivateRoute
            path={[
              '/mfe',
              '/forms/:id',
              '/forms/create',
              '/forms/view/:id',
              '/forms/edit/:id',
              '/vault',
              '/integrations',
              '/savi-ai',
              '/migration',
            ]}
            component={MfeContainer}
          />
          <WithRouterModule {...props} />
        </Switch>
        {/* Different style MFE needs revision (proxy and static import map) */}
        <PrivateRoute path="/intelligence/ispm" component={RenderIspm} />
        <PrivateRoute path="/admin/ispm-data-scope-management" component={RenderIspm} />
        <PrivateRoute path="/intelligence/integrations" component={RenderIntegrations} />
        <PrivateRoute path="/agent" component={RenderAgent} />
        <PrivateRoute path="/agent-gateway" component={RenderAgentGateways} />
        <PrivateRoute path="/intelligence/policies" component={RenderPolicies} />
        <PrivateRoute path="/nhi-olcm" component={RenderNhiOlcm} />
      </Route404>
    </Router>
  );
};

export default Routes;
